import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../../utils/api';
import useSubmitGuard from '../../hooks/useSubmitGuard';
import { useDepartment } from '../../contexts/DepartmentContext';
import { BTN, BTN_XS } from '../../constants/buttons';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import TeamTaskBoard from '../teams/TeamTaskBoard';
import GagTeamSection from './GagTeamSection';
import { MILESTONE_DEFS } from './GagGanttView';
import { ganttGagDateLiveKey } from '../gantt/ganttLiveDrag';
import { rescheduleGagTask, moveGagDate, gagTaskEditPredicate } from './gagTaskPlanning';
import OwnerSubDeptFields, { FIELD_CLS, LABEL_CLS } from './OwnerSubDeptFields';
import RaSummaryCard from './RaSummaryCard';
import ConfirmDialog from '../ConfirmDialog';
import ProgressBar from '../ProgressBar';
import { FIELD_LABEL_CLASS } from '../../constants/inputs';
// 💷 THE shared money input — root `CLAUDE.md`, *Currency Inputs*: "for every
// monetary input field — import it, never re-declare one."
import CurrencyInput from '../ui/CurrencyInput';
import NumberInput from '../ui/NumberInput';
import { unreadableDaysProblem, DAYS_PROBLEM_CLASS } from '../../utils/daysEntry';
import SuggestInput from '../ui/SuggestInput';
import InfoTip from '../InfoTip';
import RenameSyncNote from './RenameSyncNote';
import OrderActionsMenu, { MENU_ITEM_NEUTRAL } from '../OrderActionsMenu';
import WarningTriangleIcon from '../ui/WarningTriangleIcon';
import { toPayloadAmount, isMalformedAmount } from '../../utils/moneyPayload';
import {
  GAG_STATUSES,
  GAG_STATUS_LABELS,
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  DAY_NIGHT_OPTIONS,
  INT_EXT_OPTIONS,
  DAY_NIGHT_LABELS,
  INT_EXT_LABELS,
} from '../../constants/taskStatuses';
import { GAG_STATUS_COLORS, GAG_RA_BADGES, GAG_BUDGET_BADGES } from '../../constants/colors';
import { gagBudgetStatus, gagBudgetProposal } from '../../utils/gagBudgetStatus';
import { formatDate, formatDateTime, toLocalISO } from '../../utils/dateFormat';
import { formatCurrency } from '../../utils/formatCurrency';
import { diffChangedFields, dateOnly } from '../../utils/formDiff';
import { budgetPushState } from '../../utils/budgetPushState';
import PhoneActionBar from '../ui/PhoneActionBar';

/**
 * The ` (£X)` tail of the push-to-budget confirmation line — or NOTHING at all.
 *
 * ⚖️ Owner ruling 2026-09-21: *"Guard the absence marker AT THE CALL SITES.
 * Leave `formatCurrency` a dumb converter."* 🔑 **This site is why the marker
 * could never live inside `formatCurrency`: the amount is interpolated into the
 * MIDDLE of a sentence**, so a bare `—` would read *"Added to budget: Ad-hoc
 * crew (—)"* — a parenthesis around a dash, which says nothing. The marker for
 * a mid-sentence amount is the SUPPRESSION OF THE WHOLE CLAUSE, exactly as
 * `utils/gagBudgetStatus.js` drops the amount from a budgeted gag's tooltip
 * "rather than fabricating £0.00" (`components/gags/CLAUDE.md`).
 *
 * 🪤 **The absence is REACHABLE, not theoretical.** `handlePushToBudget` sets
 * the notice from `data.line?.amount` — optional chaining, so a response
 * without a `line` object yields `undefined`, and the old unguarded call on
 * that value turned it into a real, actionable `(£0.00)` on a green success
 * banner. 🪤 That old call shape is deliberately NOT quoted here: §3 of
 * `GagDetailPanel.budgetNotice.test.js` scans this file as TEXT to prove the
 * helper is wired, and a comment quoting the dead shape fails it — this
 * repo's textual scanners read comments as code.
 *
 * 🚨 THREE STATES. `null`/`undefined`/`''` are ABSENT → no clause. A present
 * but non-finite value is MALFORMED, not absent — it keeps the loud `£NaN`,
 * because a £1,000 budget can reach the browser as the string `"1,000"` and
 * reporting that as "no amount" would hide a live defect. A real, deliberate
 * `0` is finite and still renders `(£0.00)`.
 */
export function budgetAmountSuffix(amount) {
  if (amount == null || amount === '') return '';
  if (!Number.isFinite(Number(amount))) return ` (${formatCurrency(String(amount))})`;
  return ` (${formatCurrency(amount)})`;
}

// The task view always opens on Gantt when a gag is expanded (Pete, 2026-07-27)
// — deliberately NOT persisted across gags, so every expand starts on Gantt.
// Mobile has no Gantt (the tab is hidden < 768px), so it starts on List there;
// the resize effect below keeps view in sync if the viewport crosses 768px.
function initialTaskView() {
  if (typeof window !== 'undefined' && window.innerWidth < 768) return 'list';
  return 'gantt';
}

// diffChangedFields + dateOnly are shared with TaskEditor (utils/formDiff)
// so the gag form and the task form apply identical change-detection +
// date-normalisation. INVARIANT: every form value is a STRING — see the form
// factories; any new field must be normalised to a string or its edits are
// silently mis-detected by diffChangedFields and the dirty guard.

/** Editable gag form seeded from the detail payload (all values as strings). */
function gagFormFromDetail(d) {
  return {
    title: d.title || '',
    description: d.description || '',
    sub_department_id: d.sub_department_id != null ? String(d.sub_department_id) : '',
    // STRING per the diffChangedFields invariant ('' = unassigned); converted
    // to int-or-null at the API boundary in saveGag.
    owner_id: d.owner_id != null ? String(d.owner_id) : '',
    scenes: d.scenes_display || '',
    status: d.status || 'todo',
    priority: d.priority || 'medium',
    shoot_date: dateOnly(d.shoot_date),
    location_access_date: dateOnly(d.location_access_date),
    prep_date: dateOnly(d.prep_date),
    strike_date: dateOnly(d.strike_date),
    day_night: d.day_night || '',
    int_ext: d.int_ext || '',
    location: d.location || '',
    unit: d.unit || '',
    primary_effect_type: d.primary_effect_type || '',
    estimated_days: d.estimated_days != null ? String(d.estimated_days) : '',
    // STRING per the diffChangedFields invariant ('' = no estimate); converted
    // to number-or-null at the API boundary in saveGag.
    estimated_cost: d.estimated_cost != null ? String(d.estimated_cost) : '',
    // STRING per the diffChangedFields invariant — the checkbox maps to
    // '0'/'1' and the server's strict normalizer accepts '0'/'1' strings.
    requires_risk_assessment: Number(d.requires_risk_assessment) ? '1' : '0',
  };
}

/** Read-only field display for users without edit rights. */
// PROTOTYPE 2026-09-29 — owner: "Each label needs a tool tip to explain what
// it is". ONE copy of each explanation, read by the editor AND the read-only
// summary so the two can never explain a field differently.
export const GAG_FIELD_TIPS = {
  title: 'A short name for the gag. It is what everyone sees in the Breakdown list.',
  description: 'Anything the team needs to know: what the effect does, the kit involved, safety notes.',
  status: 'Where this gag is up to. Move it on as the work progresses.',
  priority: 'How urgent this gag is compared with the others in the department.',
  estimated_days: 'Roughly how many working days this gag will take your team. Part days are fine: 0.5 is half a day, 0.25 is a quarter of a day. For planning only; it does not change the budget.',
  estimated_cost: 'What you expect this gag to cost. Only people who can see the department budget see this figure.',
  scenes: 'The script scene numbers this gag appears in, separated with commas (for example 45, 47A, 102).',
  int_ext: 'Whether the scene is set inside (INT) or outside (EXT), as written in the script.',
  location: 'Where the scene is filmed.',
  day_night: 'The time of day the scene is set, as written in the script.',
  unit: 'Which shooting unit films this gag, for example Main Unit or Second Unit.',
  primary_effect_type: 'The main type of effect, for example Rain, Smoke or Pyro. Pick one used before or type a new one.',
  shoot_date: 'The day this gag is filmed. A coloured dot in the Breakdown warns you as it gets close.',
  location_access_date: 'The first day your team can get into the location to set up.',
  prep_date: 'When preparation for this gag needs to start.',
  strike_date: 'When the rig comes down and the location is cleared after filming.',
  sub_department: 'Which part of your department looks after this gag.',
  owner: 'The person responsible for this gag. They can edit it.',
  requires_risk_assessment: 'Tick if this gag needs a risk assessment. The RA column then shows whether one is linked and signed off.',
};

// (Superseded same day: the caption TEXT carries the tooltip, no ⓘ — owner:
// "I like the tool tips but dont like the info symbol".)
function TipLabel({ tip, children }) {
  return (
    <label className={LABEL_CLS}>
      {tip ? <InfoTip title={tip} label={children} /> : children}
    </label>
  );
}

function ReadOnlyField({ label, tip, children }) {
  return (
    <div>
      <span className={FIELD_LABEL_CLASS}>{tip ? <InfoTip title={tip} label={label} /> : label}</span>
      <span className="text-sm text-gray-800 dark:text-gray-200">{children ?? '-'}</span>
    </div>
  );
}

/**
 * Expanded gag detail panel (Task 3.2) — rendered inside the breakdown
 * table's expansion row. Lazy-loads the full detail (list rows lack
 * tasks/audit), shows a gag field editor (can_edit gated) beside a
 * List | Kanban | Gantt switcher over the gag's child tasks, an inline
 * add-task input, and a collapsed-by-default Activity (audit) section.
 *
 * Props:
 *  - gag: the list row (id + department_id are what we need pre-fetch)
 *  - userId: current user id (task creator guard, mirrors server)
 *  - subDepts: [{id, name}] for the sub-dept selects
 *  - showDirtyWarning: GagGroupTable's failed-Save-and-close pulse (amber);
 *    the Timeline passes none since it asks (2026-10-07)
 *  - onDirtyChange(bool): report unsaved-changes state to the parent
 *  - onChanged(): silent list refetch (row columns go stale after saves)
 *
 * Layout: stacked panel — two collapsible full-width sections sharing the
 * same slate header pattern: "Gag details" (the editor) above "Task planning"
 * (the List/Kanban/Gantt board). BOTH default collapsed on every expand; each
 * body stays MOUNTED while collapsed (CSS `hidden`), so a dirty gag form
 * survives the collapse. Folding "Task planning" CLOSES the open task, asking
 * first when its edit is dirty (owner 2026-10-19), so it never folds dirty.
 */
export default function GagDetailPanel({
  gag,
  userId,
  subDepts,
  showDirtyWarning,
  onDirtyChange,
  onChanged,
  // OPTIONAL (2026-09-29, ask-then-continue). `submitRef.current` is published
  // as `() => Promise<boolean>`: THIS panel's own save (`saveGag`, the Save
  // button's path — never a second one), true when nothing is left unsaved.
  // The parent's `Save and close` calls it. The OrderForm `submitRef` device.
  submitRef = null,
  // OPTIONAL (2026-10-07, the Timeline's ask-then-continue). `discardRef.current`
  // is published as `() => void`: drops this panel's unsaved work — its OWN
  // form (Cancel's `cancelEdit`) and an inline task edit (the board's discard,
  // TaskEditor's `revert`) — so the parent's `Discard changes` leaves it clean
  // and the held click can run. A shared draft is the row's to discard.
  discardRef = null,
  // OPTIONAL: an in-app link clicked inside the panel is handed to this
  // (`to` = the link's root-relative href) instead of navigating, so the parent can ask
  // about unsaved work first. Omitted, links behave as plain links.
  onLinkNavigate = null,
  // OPTIONAL (2026-09-29, owner: a change here must show in the main row).
  // `{ form, dirty, saving, error, savedSignal, onChange(updater),
  // onSave() → Promise<boolean>, onDiscard() }` — the Breakdown row's ONE
  // draft for this gag (`GagGroupTable`, helpers in `GagRowEditCell.js`). When
  // given, the editor renders and edits THAT draft, Save/Cancel save/discard it
  // (one diff-only PUT for row + panel fields), and this panel no longer
  // reports form dirt of its own — only an inline TASK edit. Omitted (the
  // Timeline's inline panel, a row with no draft) → the panel's own form.
  sharedDraft = null,
  // OPTIONAL (owner 2026-10-05): `false` = no Save/Discard from `md` up,
  // because the desktop Table's "Editing this row" bar is that gag's save there.
  // `true` (the Timeline) = the pair in the panel's header row (2026-10-07).
  desktopSaveBar = true,
  // OPTIONAL (2026-10-18, owner: "editing directly in the table row for table
  // row items and the Gag Details section will be for additional details").
  // A Set of panel form field names the EDITING desktop row renders a control
  // for (`GagRowEditCell#gagRowControlledPanelFields` — derived, one rule). The
  // editor omits them; they stay in the shared draft (dirt, Save, Discard and
  // the diff-only PUT unchanged). The Sub-department ↔ Owner pair is omitted
  // only as a PAIR (a non-manager's row has no owner control, so they keep
  // both — their only way to release a gag they own). Also: no panel dirty
  // chip, the row's "Editing this row" bar carries the one chip. Honoured only
  // WITH `sharedDraft`. Omitted (phone, Timeline, no draft) → the full panel.
  rowEditedFields = null,
  // OPTIONAL (2026-10-11): a COUNTER the parent bumps when this gag changed
  // OUTSIDE the panel (the Timeline's shoot-diamond drag) — the panel re-reads
  // its detail at once, so Task planning shows the new date without closing
  // and reopening the gag. Seeded from the mount value (mounting fetches
  // nothing extra). An unsaved form is NOT reseeded: the user's draft stays.
  refreshSignal = null,
  // OPTIONAL (2026-10-14, the Labour view's task click): open on this task —
  // Task planning expanded, the List view, that task's editor open. Read on
  // MOUNT only; the caller keys the panel on it to open a different task.
  openTaskId = null,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The panel's OWN form — used only when no `sharedDraft` is supplied (see
  // the prop). With one, the form, dirt, save and error are the row's.
  const [ownForm, setOwnForm] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [ownSaving, setSaving] = useState(false);
  const [ownSaveError, setSaveError] = useState(null);
  const shared = sharedDraft || null;
  const form = shared ? shared.form : ownForm;
  const setForm = shared ? shared.onChange : setOwnForm;
  // What the editing desktop row already edits (see the prop) — never without
  // a shared draft, so a panel with its own form is always complete.
  const rowEdits = shared && rowEditedFields ? rowEditedFields : null;
  const showField = (f) => !rowEdits || !rowEdits.has(f);
  const rowEditsOwnerPair = !showField('sub_department_id') && !showField('owner_id');
  // Status + Priority gone → Estimated days takes ONE track, so at md the row
  // reads Days · Cost · budget cell (1+1+2 of 4) with no orphaned track.
  const workflowCompact = !showField('status') && !showField('priority');
  const saving = shared ? shared.saving : ownSaving;
  const saveError = shared ? shared.error : ownSaveError;
  // Unreadable "Estimated days" (input audit #6) — shown under the box; both Saves refuse it.
  const daysProblem = form ? unreadableDaysProblem('Estimated days', form.estimated_days) : null;

  const [view, setView] = useState(() => (openTaskId != null ? 'list' : initialTaskView()));
  // The task board's header slot the team's add input is portalled into.
  const [teamAddSlot, setTeamAddSlot] = useState(null);
  const [units, setUnits] = useState([]);
  const [effectTypes, setEffectTypes] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);

  // Section collapse — NOT persisted; BOTH sections default COLLAPSED on every
  // expand. This component mounts per gag-expand (see the parent's dirty-guard
  // unmount), so these useState defaults reset each time — no smart auto-expand.
  // A freshly expanded gag shows two collapsed headers: "Gag details" +
  // "Task planning". The board inside "Task planning" stays MOUNTED while the
  // section is collapsed (CSS `hidden`) — Kanban/Gantt and the dirty-guard
  // wiring depend on it — but folding it first closes the open task, asking
  // when dirty (`togglePlanning`), so no task edit is ever folded away.
  const [sectionCollapsed, setSectionCollapsed] = useState(true);
  const [planningCollapsed, setPlanningCollapsed] = useState(openTaskId == null);
  // Risk Assessments is the 3rd collapsible section — same collapsed-by-default
  // rule. Drives each RaSummaryCard's `active` prop, so the per-RA risk summary
  // only lazy-fetches once the section is opened (no network calls while closed).
  const [raCollapsed, setRaCollapsed] = useState(true);

  const [taskError, setTaskError] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);

  // 🔻 "Create team for this gag" (Round 5) — its button, Keep/Take dialog and
  // notices — is GONE from the panel since 2026-10-02 (Gags/teams/tasks
  // Stage 1: a gag's team is now `gag_team_members`, managed in the Team strip
  // of Task planning — `GagTeamSection`). The server's /create-team route stays
  // until Stage 2 deletes the old team code. Pre-change SHA 2b2aafc7.

  // ── Add to budget (buyer/supervisor/admin) ──────────────────
  // Pushes the gag's saved estimated_cost into its JOB CODE's budget in the
  // department's active version (job cost codes 2026-10-28; was an ad-hoc line). budgetWarn is the same amber-pulse
  // "save first" signal createTeam uses: a dirty form holds an unsaved cost,
  // so the click fires the pulse instead of pushing a stale amount.
  const [budgetBusy, setBudgetBusy] = useState(false);
  const [budgetError, setBudgetError] = useState(null);
  // Untick → confirm → DELETE. true = the "remove this line" dialog is open.
  const [confirmUnbudget, setConfirmUnbudget] = useState(false);
  const [budgetNotice, setBudgetNotice] = useState(null); // { created, label, amount }
  const [budgetWarn, setBudgetWarn] = useState(false);
  useEffect(() => {
    if (!budgetWarn) return undefined;
    const timer = setTimeout(() => setBudgetWarn(false), 2000);
    return () => clearTimeout(timer);
  }, [budgetWarn]);

  // Tracks whether a detail payload has ever loaded — lets the fetch error
  // path distinguish "initial load failed" (full error view) from "background
  // refresh failed" (keep the loaded panel; a dirty form must NEVER be
  // silently replaced by an error view whose Retry would reseed over it).
  const hasDetailRef = useRef(false);

  // ── Detail fetch — seedForm only on first load and after gag saves, so a
  // task mutation's silent refresh can never clobber a dirty gag form.
  const fetchDetail = useCallback(async ({ silent = false, seedForm = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiGet(`/api/gags/${gag.id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      hasDetailRef.current = true;
      setDetail(data);
      if (seedForm) {
        const f = gagFormFromDetail(data);
        setOwnForm(f);
        setSnapshot(f);
      }
    } catch {
      if (silent && hasDetailRef.current) {
        // Background refresh failed with a panel already on screen — keep
        // all current state (incl. any dirty form) and surface a notice.
        setTaskError('Could not refresh gag details — showing the last loaded data.');
      } else {
        setError('Failed to load gag details');
      }
    }
    setLoading(false);
  }, [gag.id]);

  useEffect(() => { fetchDetail({ seedForm: true }); }, [fetchDetail]);

  // Autocomplete + assignee sources — best-effort, empty on failure.
  // department_id matters for global admins (no dept assignments).
  useEffect(() => {
    (async () => {
      const deptQs = gag.department_id ? `?department_id=${gag.department_id}` : '';
      try {
        const [effectsRes, usersRes] = await Promise.all([
          apiGet('/api/gags/effect-types'),
          apiGet(`/api/gags/assignable-users${deptQs}`),
        ]);
        if (effectsRes.ok) {
          const d = await effectsRes.json();
          setEffectTypes(Array.isArray(d) ? d : []);
        }
        if (usersRes.ok) {
          const d = await usersRes.json();
          setAssignableUsers(Array.isArray(d) ? d : []);
        }
      } catch { /* non-fatal */ }
    })();
  }, [gag.department_id]);

  // Units for the panel's own Unit box — fetched ONLY while that box is shown.
  // An editing desktop row owns the Unit field (`rowEdits`) and fetches the
  // same list for its own cell, so a hidden box fetching too made one opened
  // row cost two requests (measured 2026-10-19; pinned by
  // `GagGroupTable.unitRequestCount.test.js`).
  const showUnitBox = showField('unit');
  useEffect(() => {
    if (!showUnitBox) return undefined;
    let live = true;
    apiGet('/api/gags/units')
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => { if (live && Array.isArray(d)) setUnits(d); })
      .catch(() => { /* non-fatal: suggestions are best-effort */ });
    return () => { live = false; };
  }, [showUnitBox]);

  // ── Dirty tracking → parent (blocks collapse per the expand-to-edit pattern)
  const ownDirty = useMemo(() => {
    if (!ownForm || !snapshot) return false;
    return JSON.stringify(ownForm) !== JSON.stringify(snapshot);
  }, [ownForm, snapshot]);
  const isDirty = shared ? !!shared.dirty : ownDirty;

  // A nested inline TASK edit (List view expand-to-edit) is unsaved work too —
  // fold it into what we report to the parent so collapsing the whole gag panel
  // row is blocked/guarded rather than silently discarding the task edit.
  const [taskInlineDirty, setTaskInlineDirty] = useState(false);
  // A shared draft's dirt is the ROW's to report (one edit, not two).
  const dirtyForParent = (shared ? false : isDirty) || taskInlineDirty;

  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => { onDirtyChangeRef.current = onDirtyChange; });
  useEffect(() => { onDirtyChangeRef.current?.(dirtyForParent); }, [dirtyForParent]);
  // Unmount (group collapsed / list refiltered) must clear the parent guard.
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  // ── View switcher (session-only, not persisted) + mobile Gantt fallback.
  // A manual switch to List/Kanban lasts only while this panel is open — the
  // next gag you expand opens on Gantt again (see initialTaskView).
  const changeView = (v) => {
    setView(v);
  };

  useEffect(() => {
    const checkMobileGantt = () => {
      // Force-switch without persisting so a desktop preference survives.
      if (view === 'gantt' && window.innerWidth < 768) setView('list');
    };
    checkMobileGantt();
    window.addEventListener('resize', checkMobileGantt);
    return () => window.removeEventListener('resize', checkMobileGantt);
  }, [view]);

  // ── Gag editor save/cancel ──────────────────────────────────
  const setField = (field) => (e) => setFieldValue(field)(e.target.value);
  // `ui/SuggestInput` hands back a STRING, not an event.
  const setFieldValue = (field) => (v) => setForm((p) => ({ ...p, [field]: v }));

  // Resolves true when the form is saved (or had nothing to save), false on
  // any refusal — `submitRef` below reads it; the Save button ignores it.
  const saveGag = async () => {
    // Shared draft: the row's ONE save (validation, the PUT, the re-seed and
    // this panel's detail refresh via `savedSignal` all live there).
    if (shared) return saving ? false : shared.onSave();
    if (saving || !form || !snapshot) return false;
    if (!form.title.trim()) { setSaveError('Title is required'); return false; }
    // 🚨 REFUSE UNREADABLE MONEY BEFORE THE REQUEST (2026-09-23). This column
    // is NULLABLE, so the builder below answers `null` for blank AND for
    // malformed — and the server reads `null` as "clear". `12abc` typed over a
    // stored estimate therefore ERASED it and reported success. Only a
    // validator can tell the two apart; same shape as `BudgetCostCodes.js`.
    if (isMalformedAmount(form.estimated_cost)) {
      setSaveError('Estimated cost must be a plain number — for example 1000, not 1,000.');
      return false;
    }
    // Same for unreadable DAYS (input audit #6): '' would CLEAR a stored estimate.
    const daysError = unreadableDaysProblem('Estimated days', form.estimated_days);
    if (daysError) { setSaveError(daysError); return false; }
    const payload = diffChangedFields(form, snapshot, { trimFields: ['title'] });
    if (Object.keys(payload).length === 0) return true;
    // owner_id rides the all-strings form but the API contract is int-or-null
    // — sent ONLY when actually changed (diffChangedFields above).
    if ('owner_id' in payload) {
      payload.owner_id = payload.owner_id === '' ? null : parseInt(payload.owner_id, 10);
    }
    // estimated_cost rides the all-strings form but the API contract is
    // number-or-null ('' clears) — sent ONLY when actually changed. Mirrors
    // owner_id above. Number('') is 0, so the '' → null guard must stay explicit.
    if ('estimated_cost' in payload) {
      // 💷 `toPayloadAmount`, never a bare `Number` (2026-09-22). It keeps the
      // '' → null guard this line already had, and adds the two the shared
      // builder owns: a malformed value becomes `null` so the server refuses it
      // by name rather than storing a guess, and the amount is ROUNDED to 2dp.
      // 🔑 The round belongs on the payload path, not only on the input's blur:
      // blur is not the only exit from a field (Enter-submit fires none, and a
      // value hydrated from the API and saved untouched never blurs at all).
      payload.estimated_cost = toPayloadAmount(payload.estimated_cost, null);
    }

    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiPut(`/api/gags/${gag.id}`, payload);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || `Failed to save gag (HTTP ${res.status})`);
        setSaving(false);
        return false;
      }
      // The PUT succeeded — the form is no longer "unsaved" regardless of
      // whether the refresh below succeeds, so clear dirtiness immediately
      // (a failed refresh keeps last data + a notice; see fetchDetail catch).
      setSnapshot(form);
      // Refresh the full detail (scenes_display, sub-dept name, audit) and
      // silently refetch the list — the summary row shows these columns.
      await fetchDetail({ silent: true, seedForm: true });
      onChanged?.();
    } catch {
      setSaveError('Failed to save gag — check your connection.');
      setSaving(false);
      return false;
    }
    setSaving(false);
    return true;
  };

  // ── `submitRef` — the parent's `Save and close` (2026-09-29). Saves a
  // dirty inline TASK edit through the board's handle (TaskEditor's own
  // `save`, since 2026-10-07 — until then it answered false here), then the
  // gag FORM through `saveGag` when it is dirty. Any failure answers false and
  // stops there, so the parent continues nothing. Re-published every render
  // so it always closes over the current form; the cleanup clears the ref
  // only if it still holds THIS panel's function.
  // 2026-10-18: `submit({ onTaskError })` — a refused TASK save also hands its
  // message (TaskEditor's own) to `onTaskError`, so the row bar's "Save all"
  // and `Save and close` can report a partial failure exactly. Still boolean.
  const boardSaveRef = useRef(null);
  const boardDiscardRef = useRef(null);
  // ── Folding "Task planning" closes the open task (owner 2026-10-19: "if we
  // expand a task then minimise task planning all tasks should minimise"; a
  // dirty one → "Ask first"). The board publishes `boardCollapseRef`: it closes
  // its List row and any Kanban/Gantt modal, asking first through ITS OWN
  // three-way prompt (TaskEditor's save / revert — never a second path), and
  // only then calls back to fold. Keep editing / a failed save → no fold.
  // Opening the section is unchanged.
  const boardCollapseRef = useRef(null);
  const togglePlanning = () => {
    if (planningCollapsed) { setPlanningCollapsed(false); return; }
    const fold = () => setPlanningCollapsed(true);
    if (boardCollapseRef.current) boardCollapseRef.current(fold);
    else fold();
  };
  useEffect(() => {
    if (!submitRef) return undefined;
    const submit = async ({ onTaskError } = {}) => {
      if (taskInlineDirty) {
        const saveTask = boardSaveRef.current;
        const taskOk = saveTask ? await saveTask({ onError: onTaskError }) : false;
        // The refusal is written in the task editor — open the section.
        if (!taskOk) { setPlanningCollapsed(false); return false; }
      }
      // A shared draft is saved by the row before this is asked.
      if (shared || !isDirty) return true;
      const ok = await saveGag();
      // The refusal is written in the editor — open it so it can be read.
      if (!ok) setSectionCollapsed(false);
      return ok;
    };
    submitRef.current = submit;
    return () => { if (submitRef.current === submit) submitRef.current = null; };
  });

  // ── In-panel app links (2026-09-29). Capture phase, so it runs before the
  // router `<Link>`'s own click handler, which then sees `defaultPrevented`
  // and does not navigate. Only a plain left-click on a root-relative app path
  // (what a router `<Link>` renders) is intercepted — new-tab/modifier clicks,
  // `target`/`download` links, absolute URLs and file/API paths behave exactly
  // as before.
  const interceptLinkClick = (e) => {
    if (!onLinkNavigate || e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || a.target || a.hasAttribute('download')) return;
    const href = a.getAttribute('href') || '';
    if (!href.startsWith('/') || href.startsWith('//') || /^\/(api|uploads)\//.test(href)) return;
    e.preventDefault();
    onLinkNavigate(href);
  };

  const cancelEdit = () => {
    if (shared) { shared.onDiscard(); return; }
    if (snapshot) setForm(snapshot);
    setSaveError(null);
  };

  // ── `discardRef` (see the prop). Same publish/cleanup shape as `submitRef`.
  useEffect(() => {
    if (!discardRef) return undefined;
    const discard = () => {
      if (!shared && isDirty) cancelEdit();
      boardDiscardRef.current?.();
    };
    discardRef.current = discard;
    return () => { if (discardRef.current === discard) discardRef.current = null; };
  });

  // A save of the shared draft (from the row OR here) → re-read the detail
  // (audit, names). Seeded from the MOUNT value, so mounting fetches nothing
  // extra; own form reseeded too so a later fallback is never stale.
  const savedSignal = shared ? shared.savedSignal : null;
  const prevSavedSignal = useRef(savedSignal);
  useEffect(() => {
    if (savedSignal == null || savedSignal === prevSavedSignal.current) return;
    prevSavedSignal.current = savedSignal;
    fetchDetail({ silent: true, seedForm: true });
  }, [savedSignal, fetchDetail]);

  // ── `refreshSignal` (see the prop). Same seeded-counter shape as
  // `savedSignal` above. `seedForm` only while CLEAN — the detail (and so the
  // Task planning milestones) always refreshes; a dirty form is left alone.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const prevRefreshSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal == null || refreshSignal === prevRefreshSignal.current) return;
    prevRefreshSignal.current = refreshSignal;
    fetchDetail({ silent: true, seedForm: !isDirtyRef.current });
  }, [refreshSignal, fetchDetail]);

  // ── Double-submit guards (2026-08-13, `hooks/useSubmitGuard.js`). The
  // `budgetBusy` checks below are STATE flags — set before the first await,
  // so the second click of a fast double-click reads the old value and
  // double-fires (two budget pushes). The guards' refs close that
  // synchronously; the busy states stay as the buttons' UI.
  const { guard: guardPushBudget } = useSubmitGuard();
  const { guard: guardRemoveBudget } = useSubmitGuard();
  const { guard: guardProposal } = useSubmitGuard();

  // Review F4 (2026-09-29): both budget routes echo the fresh, server-thinned
  // gag row. Merge it into `detail` on success so the checkbox reflects the
  // write even when the silent refetch that follows fails (which keeps the
  // last detail by design). Only the gag's own fields — tasks, audit and RAs
  // are not on the echo and are left as they are; the form is never reseeded.
  const mergeEchoedGag = (echoed) => {
    if (!echoed || typeof echoed !== 'object' || echoed.id !== gag.id) return;
    setDetail((prev) => (prev ? { ...prev, ...echoed } : prev));
  };
  const { guard: guardCreateTask } = useSubmitGuard();

  // ── Add to budget — POST /:gagId/push-to-budget (buyer/supervisor/admin) ──
  // Idempotent server-side (one linked line per gag, updated on re-push). Dirty
  // form → pulse "save first" (an unsaved cost mustn't be pushed as stale); no
  // estimate → the button is disabled with a hint. Errors (400/403/409/423)
  // surface the server's message inline rather than being swallowed.
  const pushToBudget = () => {
    if (budgetBusy) return;
    if (isDirty) { setBudgetWarn(true); return; }
    if (detail.estimated_cost == null) return; // guarded by disabled; belt-and-braces
    return guardPushBudget(async () => {
    setBudgetBusy(true);
    setBudgetError(null);
    try {
      const res = await apiPost(`/api/gags/${gag.id}/push-to-budget`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setBudgetError(data.error || `Failed to add to budget (HTTP ${res.status})`);
      } else {
        setBudgetNotice({
          created: !!data.created,
          code: data.line?.code,
          label: data.line?.label,
          amount: data.line?.amount,
        });
        // The response's own `gag` first, so the tick is right even if the
        // refetch below fails (review F4); then refresh silently for the new
        // audit entry. No seedForm (the push is unreachable while dirty).
        mergeEchoedGag(data.gag);
        await fetchDetail({ silent: true });
        onChanged?.();
      }
    } catch {
      setBudgetError('Failed to add to budget — check your connection.');
    }
    setBudgetBusy(false);
    });
  };

  // ── Remove from budget — DELETE /:gagId/push-to-budget (owner ruling
  // 2026-09-29: "unticking … removes the gag's budget line" — since
  // 2026-10-28 its job code's budget in the active version). Same server gate as the push. Runs only from the ConfirmDialog's
  // confirm; NOT dirty-guarded — a removal sends nothing from the form, and the
  // silent refresh below does not re-seed it, so unsaved edits survive.
  // `removed: false` (already gone, e.g. removed on Manage Budgets) is still a
  // success: the refresh un-ticks the box to match the server.
  const removeFromBudget = () => guardRemoveBudget(async () => {
    setBudgetBusy(true);
    setBudgetError(null);
    setBudgetNotice(null);
    try {
      const res = await apiDelete(`/api/gags/${gag.id}/push-to-budget`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setBudgetError(data.error || `Failed to remove from budget (HTTP ${res.status})`);
      } else {
        mergeEchoedGag(data.gag); // unticks even if the refetch below fails
        await fetchDetail({ silent: true });
        onChanged?.();
      }
    } catch {
      setBudgetError('Failed to remove from budget — check your connection.');
    }
    setBudgetBusy(false);
    setConfirmUnbudget(false);
  });

  // ── THE BUDGET PROPOSAL (owner ruling 2026-09-30): the job code's budget
  // (an ad-hoc line until 2026-10-28) is the SOURCE OF TRUTH; a differing saved estimate is a proposal. `accept`
  // (Update budget line) writes line.amount := estimate; `keep` (Keep budget
  // amount) writes estimate := line.amount. Same server gate as the push.
  // Dirty-guarded like the push: an unsaved estimate is not the proposal, and
  // `keep` re-seeds the form (safe only while clean).
  const settleProposal = (action) => {
    if (budgetBusy) return;
    if (isDirty) { setBudgetWarn(true); return; }
    return guardProposal(async () => {
      setBudgetBusy(true);
      setBudgetError(null);
      setBudgetNotice(null);
      try {
        const res = await apiPost(`/api/gags/${gag.id}/budget-line/${action}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setBudgetError(data.error || `Failed to update the budget (HTTP ${res.status})`);
        } else {
          mergeEchoedGag(data.gag);
          await fetchDetail({ silent: true, seedForm: action === 'keep-amount' });
          onChanged?.();
        }
      } catch {
        setBudgetError('Failed to update the budget — check your connection.');
      }
      setBudgetBusy(false);
    });
  };

  // ── Task mutations — refresh detail silently (keep gag form), then let the
  // list catch up (progress column) via onChanged.
  const afterTaskMutation = useCallback(async () => {
    await fetchDetail({ silent: true });
    onChanged?.();
  }, [fetchDetail, onChanged]);

  // Board quick-add → POST. The board owns the input/hint/busy; we own the
  // endpoint + the Gantt default-date seeding and the silent refresh. Returns
  // the Response so the board can surface a server error + keep the typed title.
  // Guarded against the fast double-click (two clicks, two task rows): a
  // gated second call returns `undefined`, which the board's documented
  // `Promise<Response|void>` contract already tolerates.
  const createTask = (title) => guardCreateTask(async () => {
    // On the Gantt view a dateless task has no bar and looks like nothing
    // happened — default it to a 1-week bar from today (local YYYY-MM-DD, no
    // TZ drift) so it shows immediately. List/Kanban adds stay dateless.
    const body = { title };
    if (view === 'gantt') {
      const today = new Date();
      const due = new Date(today);
      due.setDate(due.getDate() + 7);
      body.start_date = toLocalISO(today);
      body.due_date = toLocalISO(due);
    }
    const res = await apiPost(`/api/gags/${gag.id}/tasks`, body);
    if (res.ok) await afterTaskMutation();
    return res;
  });

  // Board inline/modal editor → PUT / DELETE. Return the Response (TaskEditor
  // reads res.ok / res.json); refresh detail + list on success.
  const updateTask = async (task, patch) => {
    const res = await apiPut(`/api/gags/${gag.id}/tasks/${task.id}`, patch);
    if (res.ok) await afterTaskMutation();
    return res;
  };

  const deleteTask = async (task) => {
    const res = await apiDelete(`/api/gags/${gag.id}/tasks/${task.id}`);
    if (res.ok) await afterTaskMutation();
    return res;
  };

  // Kanban drop → PATCH status. THROW on failure so the board reverts the
  // optimistic move; on success refresh detail + list (progress column).
  const changeStatus = async (task, status) => {
    const res = await apiPatch(`/api/gags/${gag.id}/tasks/${task.id}/status`, { status });
    if (!res.ok) throw new Error('status');
    await afterTaskMutation();
  };

  // Milestone marker drag (Task 3.3) — single gag date field move. On
  // success the editor form is reseeded ONLY when it has no unsaved edits:
  // a dirty form keeps its values (stale snapshot included) and the user
  // resolves on save — diffChangedFields only sends fields they actually
  // changed, so an untouched date input can't overwrite the dragged value.
  const handleMilestoneDrag = async (field, value) => {
    setTaskError(null);
    let saved = false;
    try {
      const res = await moveGagDate(gag.id, field, value);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setTaskError(data.error || 'Failed to move date');
      } else {
        saved = true;
      }
    } catch {
      setTaskError('Failed to move date — check your connection.');
    }
    await fetchDetail({ silent: true, seedForm: saved && !isDirty });
    onChanged?.();
  };

  // ── Risk Assessments section (Task 3.5) ────────────────────
  // Link/unlink are INDEPENDENT of gag-form dirtiness: their detail refetches
  // never pass seedForm (linking doesn't touch any form field), so a dirty
  // form keeps its edits. The link/unlink responses do NOT carry the
  // risk_assessments array — the detail refetch is what updates the list.
  const { isAdmin, isBuyer, canViewBudget, capabilities } = useDepartment();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkable, setLinkable] = useState([]);
  const [linkableLoading, setLinkableLoading] = useState(false);
  const [selectedRaId, setSelectedRaId] = useState('');
  const [raBusy, setRaBusy] = useState(false);
  const [raError, setRaError] = useState(null);
  // Pending-unlink target for the shared ConfirmDialog (null = closed). The
  // Unlink control sets this; doUnlink runs the API work on confirm.
  const [confirmUnlink, setConfirmUnlink] = useState(null);

  // Mirrors the RA read gate (riskAssessments.js GET /:id): oversight is
  // canAuthorRiskAssessments resolved in the ACTIVE department (the gag's
  // department), so an in-scope RA opens; everyone else only their OWN — the
  // server privacy-404s the rest, so a dead link would be worse than plain
  // text. ra.created_by comes from the gag detail payload. Tranche 3 increment
  // 5 — was the unscoped `isBuyer || activeRole === 'supervisor'`.
  const hasRaOversight = isAdmin || capabilities.canAuthorRiskAssessments === true;
  // Mirrors the relaxed server read gate (Task 1): oversight roles or the
  // author can open any linked RA, AND a SIGNED-OFF RA is now readable by any
  // member of the owning department — so its summary can lazy-load for everyone.
  // Drafts still privacy-404 for non-author/non-oversight, so they stay plain.
  const canOpenRa = (ra) =>
    hasRaOversight || (ra.created_by != null && ra.created_by === userId) || ra.status === 'signed_off';

  const fetchLinkable = useCallback(async () => {
    setLinkableLoading(true);
    try {
      const res = await apiGet(`/api/gags/${gag.id}/linkable-risk-assessments`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLinkable(Array.isArray(data) ? data : []);
    } catch {
      setRaError('Failed to load linkable risk assessments — check your connection.');
    }
    setLinkableLoading(false);
  }, [gag.id]);

  const openLinkPicker = () => {
    setRaError(null);
    setSelectedRaId('');
    setLinkOpen(true);
    fetchLinkable(); // fetch on open + refetched after link/unlink — never stale
  };

  const linkRa = async () => {
    const raId = parseInt(selectedRaId, 10);
    if (!raId || raBusy) return;
    setRaBusy(true);
    setRaError(null);
    try {
      const res = await apiPost(`/api/gags/${gag.id}/risk-assessments`, { risk_assessment_id: raId });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setRaError(data.error || `Failed to link risk assessment (HTTP ${res.status})`);
      } else {
        setLinkOpen(false);
        setSelectedRaId('');
        // Refetch DETAIL for the linked list (mutation response lacks it);
        // no seedForm — a dirty gag form must keep its edits. onChanged
        // silently refreshes the list so the row badge picks up ra_status.
        await fetchDetail({ silent: true });
        onChanged?.();
      }
    } catch {
      setRaError('Failed to link risk assessment — check your connection.');
    }
    setRaBusy(false);
  };

  // Runs on ConfirmDialog confirm. Same API + guards + error handling as the old
  // window.confirm gate — only the confirmation UI changed.
  const doUnlink = async (ra) => {
    if (!ra || raBusy) return;
    setRaBusy(true);
    setRaError(null);
    try {
      const res = await apiDelete(`/api/gags/${gag.id}/risk-assessments/${ra.id}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setRaError(data.error || `Failed to unlink risk assessment (HTTP ${res.status})`);
      } else {
        await fetchDetail({ silent: true }); // same no-seedForm rule as linkRa
        onChanged?.();
        if (linkOpen) fetchLinkable(); // the RA is linkable again
      }
    } catch {
      setRaError('Failed to unlink risk assessment — check your connection.');
    }
    setRaBusy(false);
    setConfirmUnlink(null);
  };

  const handleDragComplete = async (task, newStart, newDue) => {
    setTaskError(null);
    try {
      const res = await rescheduleGagTask(gag.id, task.id, newStart, newDue);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setTaskError(data.error || 'Failed to reschedule task');
      }
    } catch {
      setTaskError('Failed to reschedule task — check your connection.');
    }
    afterTaskMutation();
  };

  // ── Render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-4">
        <LoadingSpinner size="sm" message="Loading gag details..." />
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="p-4">
        <ErrorMessage error={error || 'Failed to load gag details'} onRetry={() => fetchDetail({ seedForm: true })} />
      </div>
    );
  }

  const canEdit = !!detail.can_edit;
  const tasks = Array.isArray(detail.tasks) ? detail.tasks : [];
  // Gags/teams/tasks Stage 1 — the server-computed task rights + the team.
  const canEditTasks = detail.can_edit_tasks === true;
  const canAssignTasks = detail.can_assign_tasks === true;
  const teamMembers = Array.isArray(detail.team_members) ? detail.team_members : [];
  // The assignee picker: the gag's TEAM (owner first) listed first, then the
  // rest of the department (spec "Assigning a task"). Picking someone off the
  // team adds them to it server-side (rule 4).
  const teamIds = new Set([detail.owner_id, ...teamMembers.map((m) => m.user_id)].filter((id) => id != null).map(Number));
  const assignOptions = [
    ...assignableUsers.filter((u) => teamIds.has(Number(u.id))).map((u) => ({ ...u, group: 'Team' })),
    ...assignableUsers.filter((u) => !teamIds.has(Number(u.id))).map((u) => ({ ...u, group: 'Department' })),
  ];
  // Milestone lane array fed to the board's Gantt (MILESTONE_DEFS is the single
  // source of the four gag date fields). Same shape GagGanttView built
  // internally: dated defs only, each draggable gated on the gag-level edit
  // flag. A gag with milestone dates but zero tasks still renders the Gantt
  // (the board keys the milestone-only path off this array being non-empty).
  const milestones = MILESTONE_DEFS
    .filter((def) => detail[def.field])
    .map((def) => ({ ...def, date: String(detail[def.field]).slice(0, 10), draggable: canEdit, liveKey: ganttGagDateLiveKey(gag.id, def.field) }));
  // Same definition as the list's task_total/task_done: cancelled excluded.
  const activeTasks = tasks.filter((t) => t.status !== 'cancelled');
  const doneCount = activeTasks.filter((t) => t.status === 'done').length;
  const auditLog = Array.isArray(detail.audit_log) ? detail.audit_log : [];
  const linkedRas = Array.isArray(detail.risk_assessments) ? detail.risk_assessments : [];
  const raBadge = GAG_RA_BADGES[detail.ra_status] || null;
  // £ budget-status glyph for the panel header — same shared helper + map the
  // breakdown row uses (no duplicated logic). detail.budgeted is the read-only
  // server flag; detail.estimated_cost is the coerced number-or-null.
  const budgetStatus = gagBudgetStatus({
    estimatedCost: detail.estimated_cost, budgeted: detail.budgeted,
  });
  const budgetBadge = GAG_BUDGET_BADGES[budgetStatus.state];
  const pulse = showDirtyWarning && isDirty ? 'animate-pulse' : '';

  // ── Section pieces ───────────────────────────────────────────
  // The panel is a stacked arrangement of these shared pieces. Both collapsible
  // sections stay MOUNTED (CSS `hidden`) while collapsed, so a dirty gag form
  // survives the collapse — nothing unmounts. "Task planning" closes its open
  // task before it folds (asking when dirty), so no task edit is ever hidden.
  const bCollapsed = sectionCollapsed;

  // 🔀 2026-10-18 (owner: "we also have duplicate unsaved changes. only need
  // one. thinking header is ok"): under an editing desktop row the row's bar
  // carries the chip (and the pulse), so the panel draws none — in its header
  // row AND on the folded "Gag details" title. Phone/Timeline/no draft keep it.
  const dirtyChip = isDirty && !rowEdits ? (
    <span className={`text-xs font-medium text-amber-600 dark:text-amber-400 ${pulse}`}>
      &#9888; Unsaved changes
    </span>
  ) : null;

  // ── Add-to-budget button (Task 6) — buyer/supervisor/admin only. Every role
  // that can push is ALSO a breakdown manager (canEditGag — buyers are dept
  // admins, supervisors are dept-scoped), so it lives in the editor beside the
  // £ Estimated cost field; non-pusher editors (e.g. a plain owner) get an
  // explanatory note there instead. Uses detail.estimated_cost.
  // 🔀 LR 2026-09-24: mirrors the server gate exactly — canPushGagToBudget
  // (server/helpers/breakdownAccess.js) resolves canPushBreakdownToBudget in the
  // gag's department, which is the ACTIVE one here (same premise as
  // hasRaOversight above). Was the unscoped buyer flag OR activeRole ===
  // 'supervisor', which hid the button from a department-route buyer.
  const canPushToBudget = isAdmin || capabilities.canPushBreakdownToBudget === true;
  // Same gate the app's budget route guard uses (App.js BudgetRoute:
  // isAdmin || isBuyer || canViewBudget) — only show the "View job code"
  // link to users who can actually reach /budget/manage-budgets, so it never
  // dead-ends. A pusher who lacks budget-view (edge case) still gets the button.
  const canReachBudget = isAdmin || isBuyer || canViewBudget;
  // Enablement + hint gated on whether an estimate ACTUALLY exists (saved OR
  // typed into the estimate field), NOT on generic form dirtiness — see
  // utils/budgetPushState. This is the SAME pure decision the unit test covers,
  // so the button and the test can't drift.
  const { disabled: budgetDisabled, hint: budgetHint } = budgetPushState({
    savedEstimate: detail.estimated_cost,
    formEstimate: form?.estimated_cost,
    isDirty,
    busy: budgetBusy,
  });
  // THE "Create job code" CHECKBOX ("Add to Ad-hoc budget" until 2026-09-30,
  // "Create Budget line" until the job cost codes of 2026-10-28; owner 2026-09-29: a check box rather
  // than a button). The tick READS `detail.budgeted` — the server's "an active
  // linked line exists in the current version" flag — so it reports the truth,
  // never a local guess. Ticking runs the push (POST); UNTICKING opens a danger
  // ConfirmDialog naming the line's own amount (`detail.budget_line_amount`),
  // then runs removeFromBudget (DELETE) — owner ruling the same day: "unticking
  // 'Add to Ad-hoc budget' removes the gag's budget line". A ticked box stays
  // enabled whatever the estimate: removal needs no estimate, only the gate.
  const isBudgeted = detail.budgeted === true;
  // Pending proposal — computed from the two cost fields, so a thinned caller
  // (who receives neither) can never see it (utils/gagBudgetStatus.js).
  const proposal = gagBudgetProposal({
    budgeted: detail.budgeted,
    estimatedCost: detail.estimated_cost,
    budgetLineAmount: detail.budget_line_amount,
  });
  // 🔀 2026-09-30: the TYPED estimate is amber while it differs from the line
  // — the SAME 2dp comparison as the triangle (`gagBudgetProposal`), fed the
  // form's string instead of the saved number. Unbudgeted / thinned → never.
  // 🪤 The amber pair REPLACES FIELD_CLS's gray text pair rather than being
  // appended: `index.css` forces `html.dark .text-gray-800` with !important,
  // which would beat `dark:text-amber-400` and hide the warning in dark mode.
  const typedEstimateDiffers = gagBudgetProposal({
    budgeted: detail.budgeted,
    estimatedCost: form?.estimated_cost,
    budgetLineAmount: detail.budget_line_amount,
  }).pending;
  const estimateFieldCls = typedEstimateDiffers
    ? `${FIELD_CLS.replace('text-gray-800 dark:text-gray-200', 'text-amber-700 dark:text-amber-400')} sfx-input-warn`
    : FIELD_CLS;
  const budgetTitle = isBudgeted
    ? 'This gag has a job code with a budget. Untick to remove its budget.'
    : (budgetHint || 'Creates this gag’s job code (J01, J02 …) with its estimated cost as the budget. Orders for this job are coded to it.');
  const budgetCheckboxDisabled = isBudgeted ? budgetBusy : budgetDisabled;
  // 🔀 2026-09-30 UI tidy (owner): the checkbox reads "Create Budget line";
  // once ticked, that TEXT becomes the link to the job codes. 🔑 The
  // checkbox is a SEPARATE control (aria-label, no wrapping <label>) so a click
  // on the link can never toggle it. The three follow-up actions live in one
  // compact `Actions ▾` (the shared OrderActionsMenu), each item under the gate
  // its old button/link had, and the menu renders only when one survives.
  const budgetLabelText = budgetBusy
    ? (confirmUnbudget ? 'Removing from budget…' : 'Adding to budget…')
    : 'Create job code';
  const budgetMenuItems = [
    proposal.pending && detail.estimated_cost != null && {
      key: 'accept',
      label: 'Update job code budget',
      onClick: () => settleProposal('accept-estimate'),
      disabled: budgetBusy,
      title: 'Set the job code’s budget to this gag’s saved estimated cost.',
      testId: `gag-budget-accept-${gag.id}`,
    },
    proposal.pending && {
      key: 'keep',
      label: 'Keep budget amount',
      onClick: () => settleProposal('keep-amount'),
      disabled: budgetBusy,
      title: 'Keep the job code’s budget as it is and set this gag’s estimate back to it.',
      testId: `gag-budget-keep-${gag.id}`,
    },
    // Only for users who can actually open the budget page (canReachBudget).
    isBudgeted && canReachBudget && {
      key: 'view',
      render: (close) => (
        <Link
          role="menuitem"
          to="/budget/manage-budgets#budget-cost-codes"
          onClick={() => close()}
          title="Open the department's job codes on the Manage Budgets page."
          data-testid={`gag-budget-view-${gag.id}`}
          className={MENU_ITEM_NEUTRAL}
        >
          View job code
        </Link>
      ),
    },
  ].filter(Boolean);
  const budgetControl = canPushToBudget ? (
    <div className="flex flex-col gap-1">
      {/* `min-h-[38px]` = the estimate input's height (FIELD_CLS: py-2 +
          text-sm + 1px borders), so `items-center` centres the checkbox and
          the menu on the INPUT's midline — see the cell's spacer below. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-h-[38px]">
        <div
          title={budgetTitle}
          className={`flex items-center gap-2 py-2 text-sm text-gray-800 dark:text-gray-200 ${budgetWarn && isDirty ? 'animate-pulse' : ''}`}
        >
          <input
            type="checkbox"
            id={`gag-budget-checkbox-${gag.id}`}
            aria-label="Create job code"
            checked={isBudgeted || (budgetBusy && !confirmUnbudget)}
            onChange={(e) => {
              if (e.target.checked) pushToBudget();
              else if (isBudgeted) setConfirmUnbudget(true);
            }}
            disabled={budgetCheckboxDisabled}
            data-testid={`gag-budget-checkbox-${gag.id}`}
            className={`rounded border-gray-300 dark:border-gray-600 ${budgetCheckboxDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          />
          {isBudgeted && !budgetBusy && canReachBudget ? (
            <Link
              to="/budget/manage-budgets#budget-cost-codes"
              title="Open the department's job codes on the Manage Budgets page."
              data-testid={`gag-budget-line-link-${gag.id}`}
              className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              {budgetLabelText}
            </Link>
          ) : (
            <label
              htmlFor={`gag-budget-checkbox-${gag.id}`}
              className={budgetCheckboxDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
            >
              {budgetLabelText}
            </label>
          )}
        </div>
        {/* 📱 Below `md` (mobile batch item 12) the wrapper's `title` — the
            ONLY explanation of why this checkbox is disabled — has no hover
            to show it, so a DISABLED box shows the same string as visible
            text. Not an InfoTip ⓘ: the owner ruled the ⓘ off this panel
            (2026-09-29, "dont like the info symbol"; pinned by
            `GagDetailPanel.denseLayout.test.js` §1–§2), and a label-mode tip
            here would sit on the checkbox's own `<label>`. md+ unchanged. */}
        {budgetTitle && budgetCheckboxDisabled && (
          <p className="md:hidden basis-full text-xs text-gray-500 dark:text-gray-400 leading-snug" data-testid={`gag-budget-help-${gag.id}`}>
            {budgetTitle}
          </p>
        )}
        {budgetMenuItems.length > 0 && (
          <OrderActionsMenu
            compact
            items={budgetMenuItems}
            ariaLabel="Job code actions"
            idPrefix={`gag-budget-actions-${gag.id}`}
            triggerTestId={`gag-budget-actions-${gag.id}`}
            panelTestId={`gag-budget-actions-panel-${gag.id}`}
            triggerClassName={`${BTN_XS.secondary} ${budgetWarn && isDirty && proposal.pending ? 'animate-pulse' : ''}`}
          />
        )}
      </div>
      {/* Below `md` a DISABLED checkbox already shows this same string as the
          `gag-budget-help-*` line above, so the italic copy is md+ only there
          (2026-10-28 phone audit: the hint read twice). */}
      {budgetHint && !isBudgeted && (
        <span className={`text-xs text-gray-400 dark:text-gray-500 italic ${budgetCheckboxDisabled ? 'hidden md:inline' : ''}`} data-testid={`gag-budget-hint-${gag.id}`}>{budgetHint}</span>
      )}
    </div>
  ) : null;

  // The panel's top line: the dirty chip, the desktop Save/Discard and the
  // budget messages. 🔻 The "N/M tasks done" rollup that led it is GONE (owner
  // 2026-10-11: "do we need the task completion bar … as its already in the
  // Task planning section?") — the count lives in the Task planning header
  // now, open or folded, and in the row's Progress column. A clean gag renders
  // nothing here, so the panel opens straight onto Gag details.
  // `dirtyChip` is null exactly when the dirty row would be empty (under an
  // editing desktop row, where `desktopSaveBar` is false too) — so it gates.
  const progressHeader = (dirtyChip || budgetError || budgetNotice) ? (
    <div className="space-y-2">
      {dirtyChip && (
      <div className="flex flex-wrap items-center gap-3">
        {dirtyChip}
        {/* From `md` up the gag's Save/Discard sit HERE, in the panel's header
            row (owner 2026-10-07, Timeline: "shouldnt they be in the Gag headder
            for desktop") — the desktop Table row bar's pair, same order, size
            and titles. A phone keeps the pinned bottom bar below. */}
        {canEdit && isDirty && desktopSaveBar && (
          <span className="hidden md:inline-flex items-center gap-2 ml-auto" data-testid="gag-panel-header-actions">
            <button type="button" onClick={cancelEdit} disabled={saving}
              title="Discard every unsaved change to this gag" className={`${BTN_XS.secondary} ${pulse}`}>
              Discard
            </button>
            <button type="button" onClick={saveGag} disabled={saving}
              title="Save every unsaved change to this gag" className={`${BTN_XS.confirm} ${pulse}`}>
              {saving ? 'Saving…' : 'Save gag'}
            </button>
          </span>
        )}
      </div>
      )}
      {budgetError && (
        <div className="px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
          {budgetError}
        </div>
      )}
      {budgetNotice && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm rounded-lg bg-green-50 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
          <span>
            {budgetNotice.created
              ? `${budgetNotice.code ? `Job code ${budgetNotice.code}` : 'Job code'} created: ${budgetNotice.label}${budgetAmountSuffix(budgetNotice.amount)}`
              : `${budgetNotice.code ? `Job code ${budgetNotice.code}` : 'Job code'} updated: ${budgetNotice.label}${budgetAmountSuffix(budgetNotice.amount)}`}
          </span>
          <Link
            to="/budget/manage-budgets#budget-cost-codes"
            className="font-semibold text-green-700 dark:text-green-300 hover:underline"
          >
            View job code &rarr;
          </Link>
          <button
            type="button"
            onClick={() => setBudgetNotice(null)}
            aria-label="Dismiss"
            className="ml-auto text-green-500 dark:text-green-400 hover:text-green-700 dark:hover:text-green-200 font-bold"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  ) : null;

  // ── Gag field editor / read-only summary ──
  // Semantic row-group grids: 3 columns on big monitors, related fields
  // grouped per row (dates: 2×2 at md, all four across at xl). Each group is
  // its own grid container, so a mid-breakpoint viewport can only wrap fields
  // WITHIN their group — cross-group reflow is structurally impossible.
  // PROTOTYPE 2026-09-29 (owner: "Status, Priority, Day/night etc dont need to
  // be so big … 5+ columns"): one dense track set for every group — 2 / 4 / 6
  // across — so a short select takes a sixth of the row, not a third. Groups
  // stay separate containers (never unrelated fields sharing a row).
  // 📱 ONE field per row below `sm` (mobile batch item 12). 🪤 Every
  // `col-span-2` child inside these grids is `sm:`-prefixed for the same
  // reason: in a 1-track grid a bare `col-span-2` CREATES an implicit second
  // column and the panel overflows the phone (it widens the page and pushes
  // the fixed bottom nav off-screen on Android).
  const groupGridCls = 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-x-3 gap-y-2';
  const datesGridCls = groupGridCls;
  // DATES — the four milestones on one row at xl, 2×2 at sm/md. One definition,
  // placed by the editor body below (its position depends on `rowEdits`).
  const datesGroup = form ? (
    <div className={datesGridCls}>
      {showField('shoot_date') && (
        <div>
          <TipLabel tip={GAG_FIELD_TIPS.shoot_date}>Shoot date</TipLabel>
          <input type="date" value={form.shoot_date} onChange={setField('shoot_date')}
            disabled={saving} className={FIELD_CLS} />
        </div>
      )}
      <div>
        <TipLabel tip={GAG_FIELD_TIPS.location_access_date}>Location access</TipLabel>
        <input type="date" value={form.location_access_date} onChange={setField('location_access_date')}
          disabled={saving} className={FIELD_CLS} />
      </div>
      <div>
        <TipLabel tip={GAG_FIELD_TIPS.prep_date}>Prep date</TipLabel>
        <input type="date" value={form.prep_date} onChange={setField('prep_date')}
          disabled={saving} className={FIELD_CLS} />
      </div>
      <div>
        <TipLabel tip={GAG_FIELD_TIPS.strike_date}>Strike date</TipLabel>
        <input type="date" value={form.strike_date} onChange={setField('strike_date')}
          disabled={saving} className={FIELD_CLS} />
      </div>
    </div>
  ) : null;
  const editorBody = canEdit && form ? (
            <>
              {saveError && (
                <div className="px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                  {saveError}
                </div>
              )}
              {/* ── Semantic row groups (Pete: 3 columns on big monitors,
                  never unrelated fields sharing a row). Each group is its
                  OWN grid container, so a mid-breakpoint viewport can only
                  ever wrap fields WITHIN their group — cross-group reflow
                  is structurally impossible. 1 col small / 2 at md / 3 at
                  xl (dates: 2×2 at md, all four across at xl). Pure layout
                  — every handler/value untouched. ── */}
              {showField('title') && (
                <div>
                  <TipLabel tip={GAG_FIELD_TIPS.title}>Title *</TipLabel>
                  <input type="text" value={form.title} onChange={setField('title')} maxLength={200}
                    disabled={saving} className={FIELD_CLS} />
                  {detail.has_job_code === true && (
                    <RenameSyncNote where="Budget" testId={`gag-panel-title-sync-note-${gag.id}`}
                      changed={String(form.title).trim() !== String(detail.title || '').trim()} />
                  )}
                </div>
              )}
              <div>
                <TipLabel tip={GAG_FIELD_TIPS.description}>Description</TipLabel>
                <textarea value={form.description} onChange={setField('description')} rows={3}
                  disabled={saving} className={`${FIELD_CLS} resize-y`} />
              </div>
              {/* WORKFLOW — status / priority / effort */}
              <div className={groupGridCls}>
                {showField('status') && (
                  <div>
                    <TipLabel tip={GAG_FIELD_TIPS.status}>Status</TipLabel>
                    <select value={form.status} onChange={setField('status')} disabled={saving} className={FIELD_CLS}>
                      {GAG_STATUSES.map((s) => (
                        <option key={s} value={s}>{GAG_STATUS_LABELS[s]}</option>
                      ))}
                    </select>
                  </div>
                )}
                {showField('priority') && (
                  <div>
                    <TipLabel tip={GAG_FIELD_TIPS.priority}>Priority</TipLabel>
                    <select value={form.priority} onChange={setField('priority')} disabled={saving} className={FIELD_CLS}>
                      {TASK_PRIORITIES.map((p) => (
                        <option key={p} value={p}>{TASK_PRIORITY_LABELS[p]}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={workflowCompact ? '' : 'md:col-span-2 xl:col-span-1'}>
                  <TipLabel tip={GAG_FIELD_TIPS.estimated_days}>Estimated days</TipLabel>
                  <NumberInput min="0.25" step="0.25" placeholder="e.g. 1.5" value={form.estimated_days}
                    onChange={setFieldValue('estimated_days')} disabled={saving} className={FIELD_CLS}
                    testId={`gag-estimated-days-input-${gag.id}`} />
                  {daysProblem && (
                    <p className={DAYS_PROBLEM_CLASS} role="alert" data-testid={`gag-estimated-days-problem-${gag.id}`}>{daysProblem}</p>
                  )}
                </div>
                <div>
                  {/* 🔻 LABEL DELIBERATELY UNCHANGED — it still reads "£ Estimated
                      cost" although the field now shows its own `£`. Removing the
                      leading glyph is an owner decision, not a tidy-up, and the
                      string may be pinned; flagged as owed work, not taken. */}
                  {/* 🔀 2026-09-30: on a BUDGETED gag the caption names the
                      LINE's amount (the source of truth) — "Current Estimate:
                      £<line>", one line, no wrap. `proposal.lineAmount` is null
                      for an unbudgeted gag AND for a thinned caller (no cost
                      fields), so neither ever sees a fabricated figure. */}
                  <TipLabel tip={GAG_FIELD_TIPS.estimated_cost}>
                    {proposal.lineAmount !== null ? (
                      <span className="whitespace-nowrap" data-testid={`gag-current-estimate-${gag.id}`}>
                        {`Current Estimate: ${formatCurrency(proposal.lineAmount)}`}
                      </span>
                    ) : '£ Estimated cost'}
                  </TipLabel>
                  {/* 💷 SHARED `ui/CurrencyInput` SINCE 2026-09-22. No `prefix`:
                      the £ arrives as part of the VALUE, and the standalone glyph
                      would add `pl-7` inside `FIELD_CLS`, a shared class this
                      panel's other controls also use.
                      🪤 `setField` expects an EVENT (`e.target.value`), so it
                      cannot be passed to this control, which emits the raw STRING
                      — hence the explicit updater. Handing `setField` straight in
                      would read `undefined.target` and throw on first keystroke.
                      ✅ THE SERVER ALREADY ROUNDS THIS COLUMN, MEASURED 2026-09-22,
                      and an earlier draft of this very comment said the opposite.
                      `routes/gags.js` runs `estimated_cost` through
                      `helpers/normalizeMoney.js#normalizeMoney`, which delegates
                      to `helpers/money.js#roundMoney` (`const rounded = n !== null
                      && n >= 0 ? roundMoney(n) : null`). So `gags.estimated_cost`
                      is 2dp on write and needs no order-path-style fix — the `£`
                      here is the only half that was missing. 🪤 Do not read the
                      order path's `moneyForStorage` as evidence that other money
                      columns are unrounded; they were checked one at a time. */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <CurrencyInput value={form.estimated_cost}
                        onChange={(v) => setForm((prev) => ({ ...prev, estimated_cost: v }))}
                        placeholder="e.g. 1200.00" disabled={saving} className={estimateFieldCls}
                        testId={`gag-estimate-input-${gag.id}`} />
                    </div>
                    {/* 🔀 2026-09-30: an amber TRIANGLE, not the "Pending budget
                        review" text tag. The glyph is now the SOLE carrier, so it
                        is a shape (not a dot), in the measured amber pair
                        `text-amber-700 dark:text-amber-400` (≥4.5:1 on this
                        panel, see budget/CrewDayGrid.js), with a `title` +
                        `sr-only` pair from ONE string. */}
                    {proposal.pending && (
                      <span
                        title={proposal.tooltip}
                        data-testid={`gag-budget-pending-${gag.id}`}
                        className="shrink-0 inline-flex text-amber-700 dark:text-amber-400"
                      >
                        <WarningTriangleIcon className="w-4 h-4" />
                        <span className="sr-only">{`Pending budget review. ${proposal.tooltip}`}</span>
                      </span>
                    )}
                  </div>
                </div>
                {/* Free space beside the cost field: the push-to-budget control for
                    buyers/supervisors/admins, or an explanatory note for other editors.
                    🔀 2026-09-30 ALIGNMENT (owner): the control sits on the
                    INPUT's row, not centred across label + input. Two parts:
                    (1) at md the cost field and this cell SHARE a row — Estimated
                    days takes two of the four tracks (row 1: Status · Priority ·
                    Days; row 2: Cost · this cell ×3), at xl everything is one
                    row (1+1+1+1+2 = 6); (2) from md up an invisible spacer the
                    height of a caption (LABEL_CLS) pushes the control down to
                    the input's top edge, and the control row is the input's
                    height. Below md this cell is its own full-width row, so no
                    spacer. A layout claim: jsdom pins the classes, a browser
                    measures the alignment. */}
                <div className={`sm:col-span-2 ${workflowCompact ? 'md:col-span-2' : 'md:col-span-3'} xl:col-span-2 flex flex-col`} data-testid={`gag-budget-cell-${gag.id}`}>
                  <span aria-hidden="true" data-testid={`gag-budget-cell-spacer-${gag.id}`}
                    className={`hidden md:block invisible ${LABEL_CLS}`}>
                    {/* Mirrors InfoTip's `inline-flex align-middle` wrapper:
                        a bare &nbsp; sat 0.8px high (measured, Chromium). */}
                    <span className="inline-flex align-middle">&nbsp;</span>
                  </span>
                  {budgetControl || (
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                      A <span className="font-medium text-gray-600 dark:text-gray-300">buyer</span> or{' '}
                      <span className="font-medium text-gray-600 dark:text-gray-300">supervisor</span> can push this
                      estimate through to a job code budget.
                    </p>
                  )}
                </div>
              </div>
              {/* Owner 2026-10-18: under an editable desktop row ("Additional
                  details") the remaining dates sit ABOVE Primary effect. The full
                  panel keeps its slugline-then-dates order. */}
              {rowEdits && datesGroup}
              {/* SCRIPT — the slugline order (Scenes · INT/EXT · Location · D/N), then unit + effect */}
              <div className={groupGridCls}>
                {showField('scenes') && (
                  <div>
                    <TipLabel tip={GAG_FIELD_TIPS.scenes}>Scenes</TipLabel>
                    <input type="text" value={form.scenes} onChange={setField('scenes')} disabled={saving}
                      placeholder="e.g. 45, 47A, 102" className={FIELD_CLS} />
                  </div>
                )}
                {showField('int_ext') && (
                  <div>
                    <TipLabel tip={GAG_FIELD_TIPS.int_ext}>INT / EXT</TipLabel>
                    <select value={form.int_ext} onChange={setField('int_ext')} disabled={saving} className={FIELD_CLS}>
                      <option value="">-</option>
                      {INT_EXT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                {showField('location') && (
                  <div>
                    <TipLabel tip={GAG_FIELD_TIPS.location}>Location</TipLabel>
                    <input type="text" value={form.location} onChange={setField('location')} disabled={saving}
                      className={FIELD_CLS} />
                  </div>
                )}
                {showField('day_night') && (
                  <div>
                    <TipLabel tip={GAG_FIELD_TIPS.day_night}>Day / Night</TipLabel>
                    <select value={form.day_night} onChange={setField('day_night')} disabled={saving} className={FIELD_CLS}>
                      <option value="">-</option>
                      {DAY_NIGHT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                {showField('unit') && (
                  <div>
                    <TipLabel tip={GAG_FIELD_TIPS.unit}>Unit</TipLabel>
                    <SuggestInput value={form.unit} onChange={setFieldValue('unit')} disabled={saving}
                      options={units} inputClassName={FIELD_CLS} ariaLabel="Unit" listLabel="Units"
                      testId={`gag-panel-unit-${gag.id}`} />
                  </div>
                )}
                <div>
                  <TipLabel tip={GAG_FIELD_TIPS.primary_effect_type}>Primary effect</TipLabel>
                  <SuggestInput value={form.primary_effect_type} onChange={setFieldValue('primary_effect_type')}
                    disabled={saving} options={effectTypes} inputClassName={FIELD_CLS}
                    ariaLabel="Primary effect" listLabel="Effect types"
                    testId={`gag-panel-effect-${gag.id}`} />
                </div>
              </div>
              {!rowEdits && datesGroup}
              {/* WHO — sub-dept + owner (linked pair, always adjacent) + RA flag */}
              <div className={groupGridCls}>
                {/* Linked Sub-department ↔ Owner pair (Round 3) — shared
                    component, same rules as the create modal. Renders two
                    grid cells, so the pair stays adjacent at every
                    breakpoint (source order + grid auto-placement). */}
                {!rowEditsOwnerPair && <OwnerSubDeptFields
                  subDepts={subDepts}
                  users={assignableUsers}
                  canManage={!!detail.can_manage}
                  // Self-claim ("Me") needs breakdown rights since 2026-10-02.
                  selfClaimAllowed={!!detail.can_manage}
                  userId={userId}
                  subDeptValue={form.sub_department_id}
                  ownerValue={form.owner_id}
                  currentOwner={detail.owner_id != null
                    ? { id: detail.owner_id, name: detail.owner_name || `User ${detail.owner_id}` }
                    : null}
                  onPatch={(patch) => setForm((p) => ({ ...p, ...patch }))}
                  disabled={saving}
                  subDeptTip={GAG_FIELD_TIPS.sub_department}
                  ownerTip={GAG_FIELD_TIPS.owner}
                />}
                <div className="sm:col-span-2">
                  {/* Ghost label keeps the toggle aligned with the selects'
                      input line on md+; hidden on mobile (single column). */}
                  {!rowEditsOwnerPair && (
                    <span className={`${LABEL_CLS} hidden md:block select-none`} aria-hidden="true">&nbsp;</span>
                  )}
                  {/* Form value is the STRING '0'/'1' (diffChangedFields
                      invariant); the server's strict normalizer accepts
                      '0'/'1' strings. */}
                  <label className="flex items-center gap-2 py-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={form.requires_risk_assessment === '1'}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, requires_risk_assessment: e.target.checked ? '1' : '0' }))
                      }
                      disabled={saving}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <InfoTip title={GAG_FIELD_TIPS.requires_risk_assessment} label="Requires Risk Assessment" />
                  </label>
                </div>
              </div>
              {/* No in-flow Save / Cancel (owner 2026-10-05, the order edit's rule):
                  they appear only once something has changed, CENTRED and pinned
                  to the bottom (`ui/PhoneActionBar`) — on a PHONE. From `md` up
                  they are the header pair above (owner 2026-10-07), or the
                  desktop Table's row bar when `desktopSaveBar` is false. */}
              {isDirty && (
                <PhoneActionBar
                  pinned
                  className="flex items-center justify-center gap-2 pt-1 md:hidden"
                  testId="gag-panel-actions"
                >
                  <button type="button" onClick={saveGag} disabled={saving}
                    title="Save the changed gag fields" className={`${BTN.confirm} ${pulse}`}>
                    {saving ? 'Saving…' : 'Save gag'}
                  </button>
                  <button type="button" onClick={cancelEdit} disabled={saving}
                    title="Discard unsaved edits" className={`${BTN.secondary} ${pulse}`}>
                    Cancel
                  </button>
                </PhoneActionBar>
              )}
            </>
          ) : (
            /* Read-only display for users without edit rights */
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{detail.title}</span>
                <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${GAG_STATUS_COLORS[detail.status] || GAG_STATUS_COLORS.todo}`}>
                  {GAG_STATUS_LABELS[detail.status] || detail.status}
                </span>
              </div>
              {detail.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{detail.description}</p>
              )}
              {/* Same semantic row groups as the editor (WORKFLOW / SCRIPT /
                  DATES / WHO) — mirrored so read-only users scan the identical shape. */}
              <div className={groupGridCls}>
                <ReadOnlyField label="Priority" tip={GAG_FIELD_TIPS.priority}>{TASK_PRIORITY_LABELS[detail.priority] || detail.priority}</ReadOnlyField>
                <ReadOnlyField label="Estimated days" tip={GAG_FIELD_TIPS.estimated_days}>{detail.estimated_days ?? '-'}</ReadOnlyField>
                <ReadOnlyField label="Estimated cost" tip={GAG_FIELD_TIPS.estimated_cost}>
                  {detail.estimated_cost != null ? formatCurrency(detail.estimated_cost) : '-'}
                </ReadOnlyField>
              </div>
              <div className={groupGridCls}>
                <ReadOnlyField label="Scenes" tip={GAG_FIELD_TIPS.scenes}>{detail.scenes_display || '-'}</ReadOnlyField>
                <ReadOnlyField label="INT / EXT" tip={GAG_FIELD_TIPS.int_ext}>{INT_EXT_LABELS[detail.int_ext] || '-'}</ReadOnlyField>
                <ReadOnlyField label="Location" tip={GAG_FIELD_TIPS.location}>{detail.location || '-'}</ReadOnlyField>
                <ReadOnlyField label="Day / Night" tip={GAG_FIELD_TIPS.day_night}>{DAY_NIGHT_LABELS[detail.day_night] || '-'}</ReadOnlyField>
                <ReadOnlyField label="Unit" tip={GAG_FIELD_TIPS.unit}>{detail.unit || '-'}</ReadOnlyField>
                <ReadOnlyField label="Primary effect" tip={GAG_FIELD_TIPS.primary_effect_type}>{detail.primary_effect_type || '-'}</ReadOnlyField>
              </div>
              <div className={datesGridCls}>
                <ReadOnlyField label="Shoot date" tip={GAG_FIELD_TIPS.shoot_date}>{formatDate(detail.shoot_date)}</ReadOnlyField>
                <ReadOnlyField label="Location access" tip={GAG_FIELD_TIPS.location_access_date}>{formatDate(detail.location_access_date)}</ReadOnlyField>
                <ReadOnlyField label="Prep date" tip={GAG_FIELD_TIPS.prep_date}>{formatDate(detail.prep_date)}</ReadOnlyField>
                <ReadOnlyField label="Strike date" tip={GAG_FIELD_TIPS.strike_date}>{formatDate(detail.strike_date)}</ReadOnlyField>
              </div>
              <div className={groupGridCls}>
                <ReadOnlyField label="Sub-department" tip={GAG_FIELD_TIPS.sub_department}>{detail.sub_department_name || '-'}</ReadOnlyField>
                <ReadOnlyField label="Assigned to" tip={GAG_FIELD_TIPS.owner}>{detail.owner_name || 'Unassigned'}</ReadOnlyField>
                <ReadOnlyField label="Requires RA" tip={GAG_FIELD_TIPS.requires_risk_assessment}>
                  {Number(detail.requires_risk_assessment) ? 'Yes' : 'No'}
                </ReadOnlyField>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                Only the gag's owner or a breakdown manager can edit these fields.
              </p>
            </div>
          );

  // ── Task views — the shared TeamTaskBoard owns the List/Kanban/Gantt
  //    switcher, the inline quick-add, the task rendering and the Kanban/
  //    Gantt-click editor modal. We inject the gag-tasks endpoints and wire
  //    the panel's session view state + collapse-guard chain. taskError
  //    (drag / milestone / refresh failures) stays here, above the board;
  //    the board surfaces its own quick-add errors internally. ──
  const taskViews = (
    <div className="space-y-3 min-w-0">
          {taskError && (
            <div className="px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
              {taskError}
            </div>
          )}
          {/* The gag's TEAM (Gags/teams/tasks Stage 1) — read by everyone in
              the department, changed by the owner or breakdown rights. */}
          <GagTeamSection
            gagId={gag.id}
            ownerId={detail.owner_id}
            ownerName={detail.owner_name}
            members={teamMembers}
            canManage={canAssignTasks && !detail.deleted_at}
            departmentUsers={assignableUsers}
            onChanged={afterTaskMutation}
            // The add input sits on the board's tab line (owner 2026-10-12).
            addSlot={teamAddSlot}
          />
          <TeamTaskBoard
            headerLeadRef={canAssignTasks && !detail.deleted_at ? setTeamAddSlot : null}
            viewTabsIdPrefix={`gag-${gag.id}-task-view`}
            tasks={tasks}
            milestones={milestones}
            groupLabel={detail.title}
            // Per-task edit predicate — the SERVER's rule 1, reported as
            // `can_edit_tasks` (owner · team · breakdown rights), OR the task's
            // creator (owner ruling 2026-10-02). The board derives Kanban/Gantt
            // drag + add rights from its baseline (a creatorless task). The
            // predicate is shared with the Timeline's expanded rows
            // (`gagTaskPlanning.js`) so the two charts cannot gate differently.
            canEdit={gagTaskEditPredicate(detail, userId)}
            // Rule 3: assigning is the owner's or breakdown rights' alone —
            // `can_assign_tasks` from the server, never re-derived.
            canAssign={canAssignTasks}
            assignPicker={{ options: assignOptions }}
            readOnlyNote=" — only the gag owner, its team, a breakdown manager or the task creator can change this task."
            // Inside a gag every row is that gag's — the Gag / From column says nothing.
            hideColumns={['context']}
            canAddTask={canEditTasks}
            assignableUsers={assignableUsers}
            subDepts={subDepts}
            // Controlled view: reset-to-Gantt-per-expand + mobile Gantt→List
            // fallback stay owned by this panel (initialTaskView + resize effect).
            view={view}
            onViewChange={changeView}
            onCreateTask={createTask}
            onUpdateTask={updateTask}
            onDeleteTask={deleteTask}
            onStatusChange={changeStatus}
            onDragComplete={handleDragComplete}
            onMilestoneDrag={handleMilestoneDrag}
            // Nested inline task-edit dirtiness folds into the panel-level
            // collapse guard (dirtyForParent → onDirtyChange).
            onDirtyChange={setTaskInlineDirty}
            showDirtyWarning={showDirtyWarning}
            // The open task editor's own save / discard, for submitRef / discardRef.
            saveRequestRef={boardSaveRef}
            discardRequestRef={boardDiscardRef}
            // Folding "Task planning" closes the open task, asking first if dirty.
            collapseRequestRef={boardCollapseRef}
            // Under an editing DESKTOP row the row bar carries the ONE chip.
            hideEditorDirtyChip={!!rowEdits}
            initialExpandedId={openTaskId}
          />
    </div>
  );

  // ── Risk Assessments (Task 3.5 / Task 2) — full transparency: read-only
  //    users see the badge + linked cards; link/unlink/create are can_edit
  //    only. Now the 3rd collapsible section (same slate-header pattern as
  //    "Task planning"): raBadge lives IN the header for at-a-glance status
  //    (mirrors the £ glyph on "Gag details"), while the link controls, picker
  //    and per-RA summary cards live in the collapsible body. ──
  const raSectionBody = (
    <>
        <div className="flex flex-wrap items-center gap-2">
          {/* Empty state rides this row (Pete: its own line wasted vertical
              space). flex-wrap stacks it naturally on narrow widths; when RAs
              ARE linked the cards below render instead. */}
          {linkedRas.length === 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic min-w-0">
              · No risk assessments linked{canEdit ? ' — link an existing RA or create a new one above.' : '.'}
            </span>
          )}
          {canEdit && (
            <div className="ml-auto flex items-center gap-2">
              {!linkOpen && (
                <button type="button" onClick={openLinkPicker} disabled={raBusy}
                  title="Link an existing risk assessment from this department to this gag"
                  className={BTN_XS.primary}>
                  Link Risk Assessment
                </button>
              )}
              {/* v1: navigate only — the user links the new RA manually
                  afterwards (no ?link_gag= return-flow). Admins can't author
                  RAs (mirrors the RA overview's canCreate gate). */}
              {!isAdmin && (
                <Link to="/risk-assessments/new" title="Create a new risk assessment (link it to this gag afterwards)"
                  className={BTN_XS.secondary}>
                  + New RA
                </Link>
              )}
            </div>
          )}
        </div>

        {raError && (
          <div className="px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
            {raError}
          </div>
        )}

        {linkOpen && (
          <div className="flex flex-wrap items-center gap-2">
            {linkableLoading ? (
              <span className="text-xs text-gray-400 dark:text-gray-500">Loading risk assessments…</span>
            ) : linkable.length === 0 ? (
              // Suppressed while raError shows: a fetch failure leaves the
              // list empty, and "No linkable risk assessments" would
              // contradict the error banner right above.
              !raError && (
                <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                  No linkable risk assessments in this department.
                </span>
              )
            ) : (
              /* Signed-off entries distinguished via optgroup + ✓ prefix */
              <select
                value={selectedRaId}
                onChange={(e) => setSelectedRaId(e.target.value)}
                disabled={raBusy}
                aria-label="Risk assessment to link"
                className={`${FIELD_CLS} w-auto max-w-full`}
              >
                <option value="">Select risk assessment…</option>
                {linkable.some((r) => r.status === 'signed_off') && (
                  <optgroup label="Signed off">
                    {linkable.filter((r) => r.status === 'signed_off').map((r) => (
                      <option key={r.id} value={r.id}>
                        ✓ {r.title}{r.reference ? ` [${r.reference}]` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {linkable.some((r) => r.status !== 'signed_off') && (
                  <optgroup label="Drafts">
                    {linkable.filter((r) => r.status !== 'signed_off').map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.title}{r.reference ? ` [${r.reference}]` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
            <button type="button" onClick={linkRa} disabled={raBusy || !selectedRaId} className={BTN_XS.confirm}>
              {raBusy ? 'Linking…' : 'Link'}
            </button>
            <button
              type="button"
              onClick={() => { setLinkOpen(false); setSelectedRaId(''); }}
              disabled={raBusy}
              className={BTN_XS.secondary}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Empty state lives on the body's top row above — only the linked
            cards render down here. Each card shows the header (title / ref /
            status) plus, for openable RAs, a lazy risk summary. `active` is
            the section's open state, so a collapsed section fetches nothing. */}
        {linkedRas.length > 0 && (
          <div className="space-y-1.5">
            {linkedRas.map((ra) => (
              <RaSummaryCard
                key={ra.id}
                ra={ra}
                canOpen={canOpenRa(ra)}
                active={!raCollapsed}
                canEdit={canEdit}
                onUnlink={(target) => setConfirmUnlink(target)}
                unlinkBusy={raBusy}
              />
            ))}
          </div>
        )}
    </>
  );

  // ── Activity (audit trail) — collapsed by default ──
  // Pete's feedback: the old bold full-width chevron bar read like another
  // expandable GAG ROW. The trigger is now a compact, muted inline text
  // disclosure (footnote weight, hover underline) under the panel's bottom
  // divider — clearly an in-panel utility, not a row/card.
  const activitySection = (
    <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
        <button
          type="button"
          onClick={() => setActivityOpen((o) => !o)}
          aria-expanded={activityOpen}
          className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:underline"
        >
          <span className={`text-[9px] transition-transform ${activityOpen ? 'rotate-90' : ''}`}>&#9654;</span>
          Activity ({auditLog.length})
        </button>
        {activityOpen && (
          auditLog.length === 0 ? (
            <p className="mt-2 pl-4 text-sm text-gray-400 dark:text-gray-500 italic">No activity recorded.</p>
          ) : (
            <ul className="mt-2 pl-4 space-y-1.5">
              {auditLog.map((entry) => (
                <li key={entry.id} className="text-xs text-gray-600 dark:text-gray-400 flex flex-wrap gap-x-2">
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {entry.performed_by_name || 'Unknown'}
                  </span>
                  <span className="font-mono">{entry.action}</span>
                  {entry.details && <span className="break-all">{entry.details}</span>}
                  <span className="text-gray-400 dark:text-gray-500 ml-auto whitespace-nowrap">
                    {formatDateTime(entry.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )
        )}
    </div>
  );

  // ── Arrangement — the actual variant layouts (thin, shared pieces only) ──
  // Two stacked collapsible sections — "Gag details" (editor) and "Task
  // planning" (the List/Kanban/Gantt board) — sharing the identical slate
  // header pattern (chevron + bar + hover + dark-mode classes). BOTH default
  // collapsed on every expand (see the section-collapse state). Each section's
  // body stays MOUNTED while collapsed (CSS `hidden`), so collapsing never
  // discards a dirty gag form (and folding "Task planning" asks about a dirty
  // task edit before it closes the task, so none is ever folded) — and the
  // board's onDirtyChange/showDirtyWarning wiring keeps feeding the parent
  // collapse-guard regardless of the "Task planning" section's own state.
  // 📱 Phone (owner 2026-10-04, "nested sections reduce width … take inspiration from My
  // Orders"): no side padding here and no card around each section — a section is its
  // slate bar with the body flush below, as an opened order is on a phone.
  return (
    <div className="p-3 max-md:px-0 md:p-4 space-y-4 min-w-0" onClickCapture={interceptLinkClick}>
      {progressHeader}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-md:rounded-[2px] max-md:border-0">
        <button
          type="button"
          aria-expanded={!bCollapsed}
          onClick={() => setSectionCollapsed(!bCollapsed)}
          className="flex items-center gap-2 w-full text-left px-4 py-2.5 cursor-pointer bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
        >
          <span className={`text-xs text-gray-500 dark:text-gray-400 transition-transform ${bCollapsed ? '' : 'rotate-90'}`}>&#9654;</span>
          {/* Owner 2026-10-18: under an editable desktop row the row edits the
              main fields, so this section holds only the rest — "Additional
              details" (his 2026-08-09 wording). Wherever the panel is the whole
              editor (phone, Timeline, Labour, read-only) it stays "Gag details". */}
          <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-gray-800 dark:text-gray-200">
            {rowEdits ? 'Additional details' : 'Gag details'}
          </span>
          {/* £ budget-status glyph — echoes the breakdown row's indicator
              (shared helper + GAG_BUDGET_BADGES map, own hover tooltip). */}
          <span className={`text-sm font-semibold ${budgetBadge.cls}`}
            title={budgetStatus.tooltip} aria-label={budgetStatus.tooltip}>£</span>
          {bCollapsed && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {detail.sub_department_name || 'No sub-department'}
              {detail.shoot_date ? ` · shoots ${formatDate(detail.shoot_date)}` : ''}
            </span>
          )}
          {bCollapsed && dirtyChip}
        </button>
        <div className={bCollapsed ? 'hidden' : 'p-3 max-md:px-0 space-y-3 bg-white dark:bg-gray-800'}>
          {editorBody}
        </div>
      </div>
      {/* Task planning — same collapsible header pattern as "Gag details".
          The board stays MOUNTED while hidden (the dirty-guard wiring needs
          it); folding closes the open task first, asking if dirty; the view still defaults to Gantt on
          each expand (initialTaskView) so opening this shows the Gantt. */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden min-w-0 max-md:rounded-[2px] max-md:border-0">
        <button
          type="button"
          aria-expanded={!planningCollapsed}
          onClick={togglePlanning}
          className="flex items-center gap-2 w-full text-left px-4 py-2.5 cursor-pointer bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
        >
          <span className={`text-xs text-gray-500 dark:text-gray-400 transition-transform ${planningCollapsed ? '' : 'rotate-90'}`}>&#9654;</span>
          <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-gray-800 dark:text-gray-200">Task planning</span>
          {/* Task progress, ALWAYS (2026-10-11: it is the panel's one count now
              that the top rollup is gone). 🔻 The folded "⚠ Unsaved task edit"
              cue is DELETED (owner 2026-10-19, "Ask first"): the fold now asks
              about a dirty task edit, so the section can never be folded dirty. */}
          <div className="flex items-center gap-1.5" data-testid="gag-planning-progress">
            <span className="text-xs text-gray-500 dark:text-gray-400">&middot;</span>
            <ProgressBar done={doneCount} total={activeTasks.length} className="w-16" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {doneCount}/{activeTasks.length}
            </span>
          </div>
        </button>
        <div className={planningCollapsed ? 'hidden' : 'p-3 max-md:px-0 bg-white dark:bg-gray-800 min-w-0'}>
          {taskViews}
        </div>
      </div>
      {/* Risk Assessments — 3rd collapsible section, same slate-header pattern
          as "Task planning". raBadge sits in the header (like the £ glyph on
          "Gag details") so RA status stays scannable while collapsed. The body
          holds the link controls, picker and per-RA summary cards; it stays
          MOUNTED while hidden, but each card only fetches once `active` (the
          expanded state) flips true — so a collapsed section makes no calls. */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden min-w-0 max-md:rounded-[2px] max-md:border-0">
        <button
          type="button"
          aria-expanded={!raCollapsed}
          onClick={() => setRaCollapsed((c) => !c)}
          className="flex items-center gap-2 w-full text-left px-4 py-2.5 cursor-pointer bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
        >
          <span className={`text-xs text-gray-500 dark:text-gray-400 transition-transform ${raCollapsed ? '' : 'rotate-90'}`}>&#9654;</span>
          <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-gray-800 dark:text-gray-200">Risk Assessments</span>
          {/* Status pill — always in the header (mirrors the £ glyph) so the
              required/linked/covered state is scannable without expanding. */}
          {raBadge ? (
            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${raBadge.cls}`}>
              {raBadge.label}
            </span>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">Not required</span>
          )}
        </button>
        <div className={raCollapsed ? 'hidden' : 'p-3 max-md:px-0 space-y-2 bg-white dark:bg-gray-800 min-w-0'}>
          {raSectionBody}
        </div>
      </div>
      {activitySection}
      <ConfirmDialog
        open={!!confirmUnlink}
        title="Unlink risk assessment"
        message={
          <>
            Unlink risk assessment &ldquo;{confirmUnlink?.title}&rdquo; from this gag?
            <br />
            <br />
            The RA itself is not deleted.
          </>
        }
        confirmLabel="Unlink"
        variant="default"
        busy={raBusy}
        onConfirm={() => { doUnlink(confirmUnlink); }}
        onCancel={() => setConfirmUnlink(null)}
      />
      {/* Untick "Create job code". Names the job code budget's own amount (the
          estimate may have moved since the last push); a caller the server
          thinned the figure from gets the sentence without it, never £0.00. */}
      <ConfirmDialog
        open={confirmUnbudget}
        title="Remove the job code budget"
        message={
          <>
            Remove this gag&rsquo;s{' '}
            {Number.isFinite(detail.budget_line_amount) ? `${formatCurrency(detail.budget_line_amount)} ` : ''}
            job code budget from this budget version?
            <br />
            <br />
            &ldquo;{detail.title}&rdquo; stays on the breakdown. Its job code and orders stay; ticking the box again sets the budget to the gag&rsquo;s estimate.
          </>
        }
        confirmLabel="Remove budget"
        variant="danger"
        busy={budgetBusy}
        onConfirm={removeFromBudget}
        onCancel={() => setConfirmUnbudget(false)}
      />
    </div>
  );
}
